import React, { useState } from 'react'
import loadable from '@loadable/component'
import { VideosLibraryProps } from '@/components/organisms/VideoLibrarySection/types'
import { SortComponent } from '@/components/organisms/VideoLibrarySection/SortComponent/SortComponent'
import MenuFilter from '@/components/organisms/VideoLibrarySection/MenuFilter/MenuFilter'
import MenuFilterMobile from '@/components/organisms/VideoLibrarySection/MenuFilter/MenuFilterMobile'

import { MarkdownContent } from '@/components/organisms/MarkdownContent/MarkdownContent'
import { filterUndefined } from '@/helpers/removeUndefined'

const PaginationComponent = loadable(
  () => import('@/components/organisms/VideoLibrarySection/Pagination/PaginationComponent'),
  { fallback: <div className="min-h-[3271px] desktop:min-h-[1810px]">Loading videos...</div> }
)

export function VideoLibrarySection({ pageContent, ...props }: VideosLibraryProps) {
  const { content } = pageContent?.blok || {}
  const {
    videos,
    categoriesLabel,
    allVideosLabel,
    featuredLabel,
    sortNewLabel,
    sortOldLabel,
    sortAbcLabel,
    heading,
    videos_per_page
  } = content || props || {}

  const sort_items = [sortNewLabel, sortOldLabel, sortAbcLabel].filter((item): item is string => item !== undefined)

  const [category, setCategory] = useState(allVideosLabel)
  const [selectedSortOption, setSelectedSortOption] = useState(sortNewLabel)

  const leftMenuItems = [...new Set(videos?.map((video) => video.category))].sort() as string[]

  const changeCategory = (categoryName: string) => {
    setCategory(categoryName)
  }

  const filterByCategory = (videosArray: VideoTypes[]) => {
    if (category === allVideosLabel) {
      return videosArray
    }
    if (category === featuredLabel) {
      return videos?.filter((video) => video.is_featured)
    }
    return videos?.filter((video) => video.category === category)
  }

  const videosFilteredByCategory = filterUndefined(filterByCategory(filterUndefined(videos)))

  const sortedVideos = videosFilteredByCategory.sort((a: VideoTypes, b: VideoTypes): number => {
    const compare = (strA: string, strB: string): number => strA.toLowerCase().localeCompare(strB.toLowerCase())

    switch (selectedSortOption) {
      case sortNewLabel:
        return b.date && a.date ? compare(b.date, a.date) || compare(b.title || '', a.title || '') : 0
      case sortOldLabel:
        return b.date && a.date ? compare(a.date, b.date) || compare(b.title || '', a.title || '') : 0
      case sortAbcLabel:
        return b.title && a.title ? compare(a.title, b.title) || compare(a.date || '', b.date || '') : 0
      default:
        return b.date && a.date ? compare(b.date, a.date) || compare(b.title || '', a.title || '') : 0
    }
  })

  return (
    <section className="flex flex-col max-w-screen-desktop mx-auto p-24 laptop:px-90 laptop:pb-64">
      <div className="flex flex-col justify-center items-center text-center w-full netbook:px-208 lg:px-288">
        {heading && <MarkdownContent content={heading[0].content} heading={heading[0].heading} />}
      </div>
      <div className="flex flex-col w-full netbook:flex-row netbook:justify-between" id="filter">
        <div className="hidden netbook:flex ">
          <MenuFilter
            menuItems={leftMenuItems}
            setCategory={changeCategory}
            allVideosLabel={allVideosLabel as string}
            featuredLabel={featuredLabel as string}
            categoriesLabel={categoriesLabel as string}
          />
        </div>
        <div className="block netbook:hidden">
          <MenuFilterMobile
            menuItems={leftMenuItems}
            setCategory={changeCategory}
            category={category}
            allVideosLabel={allVideosLabel as string}
            featuredLabel={featuredLabel as string}
            categoriesLabel={categoriesLabel as string}
          />
        </div>
        <div className="netbook:flex-2">
          <div className="flex flex-col-reverse gap-20 mt-16 tablet:flex-row justify-between items-center mb-16 netbook:mt-0">
            <h2 className=" text-headingM-mobile tablet:text-headingL-mobile text-text-dark-900 leading-9 font-bold w-full netbook:w-auto">
              {category}
            </h2>
            <SortComponent options={sort_items} sortBy={selectedSortOption as string} sortFn={setSelectedSortOption} />
          </div>
          <PaginationComponent
            items={sortedVideos}
            itemsPerPage={videos_per_page && videos_per_page > 0 ? videos_per_page : 4}
            key={category}
          />
        </div>
      </div>
    </section>
  )
}
